import { useState } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  BrainCircuit,
  Check,
  CreditCard,
  Gauge,
  Layers,
  ReceiptText,
  Scale,
  Sparkles,
  Workflow,
} from "lucide-react";
import { useAuth } from "../../../lib/auth/useAuth";
import { Marquee, Reveal, Rotator } from "../../components/public/motion";
import { BrandChips } from "../../components/public/BrandChips";
import { DashPanel } from "./demo/DashPanel";
import { AIPanel } from "./demo/AIPanel";
import { InboxPanel } from "./demo/InboxPanel";

const VERBS = [
  "Always.",
  "Today.",
  "At 3 a.m.",
  "In April.",
  "By Friday.",
  "On demand.",
] as const;

const VERDICT_AVATARS = [
  { from: "#7C3AED", to: "#4F46E5" },
  { from: "#2563EB", to: "#0891B2" },
  { from: "#059669", to: "#0D9488" },
  { from: "#D97706", to: "#DC2626" },
  { from: "#EC4899", to: "#8B5CF6" },
  { from: "#4F46E5", to: "#2563EB" },
] as const;

const VERDICTS = [
  {
    quote: "We stopped dreading tax season. Proofly just … keeps our books ready.",
    name: "Maya R.",
    role: "Owner, Salt & Fig Bakery",
  },
  {
    quote: "The audit score is the only dashboard my accountant actually opens.",
    name: "Daniel K.",
    role: "Founder, Oakline HVAC",
  },
  {
    quote: "Connected QuickBooks in one click. Every receipt has a home now.",
    name: "Priya S.",
    role: "Bookkeeper, Lantern Studio",
  },
  {
    quote: "Free for 9 seats was the unlock. We'd have quit Expensify years ago if we'd known.",
    name: "Jordan T.",
    role: "Partner, Northbay Legal",
  },
  {
    quote: "The first SMB tool that respects a 6-person team's time and budget.",
    name: "Camille D.",
    role: "COO, Fernleaf Marketing",
  },
  {
    quote: "I handed my auditor a link. She called it 'the cleanest packet I've seen.'",
    name: "Rafael M.",
    role: "CFO-for-hire",
  },
] as const;

const FEATURES = [
  {
    icon: Sparkles,
    title: "AI that closes the loop",
    body: "Categorization, extraction, reconciliation — continuous, explainable, and always cited.",
  },
  {
    icon: ReceiptText,
    title: "Receipts + invoices + mileage",
    body: "Email it, snap it, link a card. However proof arrives, Proofly files it.",
  },
  {
    icon: CreditCard,
    title: "Card-linked capture",
    body: "Connect a card once. Charges arrive already matched to the right receipt and category.",
  },
  {
    icon: Layers,
    title: "Batch review & approval",
    body: "Categorize, approve, or request info on 200 items in a single sweep.",
  },
  {
    icon: Scale,
    title: "Policies in plain English",
    body: "Write rules like you explain them. Every flag cites the policy it tripped.",
  },
  {
    icon: BrainCircuit,
    title: "Narrative generation",
    body: "Every expense gets a one-line story — the 'why,' written for an auditor.",
  },
  {
    icon: Workflow,
    title: "Approvals that flex",
    body: "Manager-first, CFO-first, or auto-approve under threshold. Mix by category.",
  },
  {
    icon: Gauge,
    title: "Live audit score",
    body: "One number. Drill down to the missing receipt, vendor, or narrative.",
  },
];

const DEMO_TABS = [
  { id: "dash", label: "Audit Dashboard" },
  { id: "ai", label: "AI Assistant" },
  { id: "inbox", label: "Inbox" },
] as const;

type DemoTab = "dash" | "ai" | "inbox";

export function Landing() {
  const { session } = useAuth();
  const ctaHref = session ? "/inbox" : "/login";
  const ctaLabel = session ? "Open app" : "Start free";
  const [demoTab, setDemoTab] = useState<DemoTab>("dash");

  return (
    <div className="w-full overflow-hidden">
      {/* ───────── HERO ───────── */}
      <section className="relative">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(900px 540px at 50% 0%, rgba(46,91,255,0.16), transparent 60%), radial-gradient(600px 400px at 80% 20%, rgba(245,158,11,0.10), transparent 60%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
          }}
        />
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-12 pb-10 md:pt-20 md:pb-24">
          <div className="grid md:grid-cols-[1fr_auto] gap-10 md:gap-16 items-center">
            {/* LEFT */}
            <div className="text-center md:text-left">
              <Reveal y={10} className="flex justify-center md:justify-start">
                <div className="inline-flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-full bg-[var(--ink-100)] border border-[var(--ink-200)]">
                  <span className="text-[13px] font-medium text-[var(--ink-600)]">Free forever</span>
                  <span className="bg-[var(--accent-amber)] text-[var(--ink-950)] text-[11px] font-bold uppercase tracking-[0.06em] px-2.5 py-1 rounded-full">
                    Teams under 10
                  </span>
                </div>
              </Reveal>

              <Reveal y={20} delay={100}>
                <h1
                  className="mt-5 md:mt-6 font-display font-bold tracking-[-0.04em] text-[var(--ink-950)]"
                  style={{ fontSize: "clamp(50px, 9vw, 80px)", lineHeight: 1.02 }}
                >
                  Audit-ready.
                  <br />
                  <Rotator
                    words={VERBS}
                    interval={3400}
                    className="bg-gradient-to-br from-[var(--brand-600)] to-[var(--brand-900)] bg-clip-text text-transparent"
                  />
                </h1>
              </Reveal>

              <Reveal y={20} delay={200}>
                <p className="mt-5 md:mt-5 text-[16px] md:text-[18px] leading-[1.6] text-[var(--ink-600)] max-w-[460px] mx-auto md:mx-0">
                  Every receipt, invoice, and transaction — captured, categorized,
                  and ready to defend. Built for the 98% of SMBs without a CFO.
                </p>
              </Reveal>

              <Reveal y={14} delay={300}>
                <div className="mt-9 md:mt-8 flex flex-col items-center md:items-start gap-2.5 md:flex-row">
                  <Link
                    to={ctaHref}
                    className="group inline-flex items-center justify-center gap-2 w-3/5 md:w-auto md:h-12 px-7 rounded-full bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white font-semibold text-[16px] md:text-base shadow-[0_12px_30px_-10px_rgba(10,15,30,0.4)] transition-all"
                    style={{ height: "3.25rem" }}
                  >
                    {ctaLabel}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center gap-2 w-3/5 md:w-auto h-12 px-6 rounded-full border border-[var(--ink-200)] bg-white hover:bg-[var(--ink-50)] text-[var(--ink-900)] font-semibold text-[16px] md:text-base transition-colors"
                  >
                    See pricing
                  </Link>
                </div>
              </Reveal>

              <Reveal y={10} delay={400}>
                <p className="mt-4 md:mt-4 text-[12px] tracking-wide text-[var(--ink-400)] text-center md:text-left">
                  No credit card · no trial clock · setup in 5 minutes
                </p>
              </Reveal>
            </div>

            {/* RIGHT */}
            <div className="hidden md:flex items-center justify-center">
              <AuditStamp score={96} />
            </div>
          </div>
        </div>
      </section>

      {/* ───────── TABBED LIVE DEMO ───────── */}
      <section className="bg-[var(--ink-50)] pt-6 pb-8 md:py-16">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <Reveal>
            {/* Tab buttons */}
            <div className="flex justify-center gap-2 mb-3">
              {DEMO_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setDemoTab(t.id)}
                  className={[
                    "px-4 py-1.5 rounded-lg text-[13px] font-medium transition-colors border",
                    demoTab === t.id
                      ? "bg-[var(--brand-50)] border-[var(--brand-200)] text-[var(--brand-700)]"
                      : "bg-white border-[var(--ink-200)] text-[var(--ink-500)] hover:bg-[var(--ink-50)]",
                  ].join(" ")}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Panel */}
            <div className="rounded-2xl border border-[var(--ink-200)] shadow-[var(--shadow-lg)] overflow-hidden h-[380px] md:h-[480px]">
              {demoTab === "dash" && <DashPanel />}
              {demoTab === "ai" && <AIPanel />}
              {demoTab === "inbox" && <InboxPanel />}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── TRUST STRIP ───────── */}
      <section className="border-y border-[var(--ink-200)] bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-6 flex flex-col items-center gap-4">
          <span className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--ink-500)]">
            Integrates with
          </span>
          <BrandChips />
        </div>
      </section>

      {/* ───────── FEATURES — HORIZONTAL RAIL ───────── */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
            <Reveal>
              <div className="max-w-[560px]">
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
                  What Proofly does
                </div>
                <h2
                  className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.02em]"
                  style={{ fontSize: "clamp(28px, 4vw, 42px)", lineHeight: 1.15 }}
                >
                  Eight moves. One complete record.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <Link
                to="/features"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-700)] hover:text-[var(--brand-600)]"
              >
                All features <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
        <Reveal y={24}>
          <div className="proofly-hscroll flex gap-4 overflow-x-auto px-[calc(50%-140px)] pb-12 -mt-6 max-w-[1200px] mx-auto">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="shrink-0 w-[280px] md:w-[320px] snap-center p-6 rounded-2xl bg-white border border-[var(--ink-200)] hover:border-[var(--brand-300)] hover:shadow-[var(--shadow-md)] transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--brand-50)] text-[var(--brand-700)] mb-4">
                  <f.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <h3 className="text-[17px] font-semibold text-[var(--ink-950)] tracking-tight mb-1.5">
                  {f.title}
                </h3>
                <p className="text-[14px] leading-relaxed text-[var(--ink-700)]">{f.body}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pb-4 -mt-6 text-[12px] text-[var(--ink-500)] flex items-center gap-2">
          <span>← scroll →</span>
        </div>
      </section>

      {/* ───────── VERDICTS — ROTATING RIBBON ───────── */}
      <section className="bg-[var(--ink-50)] border-y border-[var(--ink-200)] py-12 md:py-16">
        <Reveal>
          <div className="max-w-[1200px] mx-auto px-5 md:px-8 mb-8">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-2 text-center">
              Verdicts from the field
            </div>
            <h2
              className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.02em] text-center"
              style={{ fontSize: "clamp(24px, 3.5vw, 34px)", lineHeight: 1.2 }}
            >
              Built for the people keeping small businesses running.
            </h2>
          </div>
        </Reveal>
        <Marquee speed={55}>
          {VERDICTS.map((v, i) => (
            <figure
              key={v.name}
              className="shrink-0 w-[360px] md:w-[420px] p-6 rounded-2xl bg-white border border-[var(--ink-200)]"
            >
              <div className="text-[var(--brand-600)] text-3xl leading-none font-display mb-3">"</div>
              <blockquote className="text-[15px] leading-relaxed text-[var(--ink-900)]">
                {v.quote}
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2.5">
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${VERDICT_AVATARS[i % VERDICT_AVATARS.length].from}, ${VERDICT_AVATARS[i % VERDICT_AVATARS.length].to})`,
                  }}
                  aria-hidden="true"
                >
                  {v.name[0]}
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-[var(--ink-900)]">{v.name}</div>
                  <div className="text-[12px] text-[var(--ink-500)]">{v.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </Marquee>
      </section>

      {/* ───────── PRICING TEASER ───────── */}
      <section className="bg-[var(--ink-950)] text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #5E7FFF 0%, transparent 40%), radial-gradient(circle at 80% 80%, #F59E0B 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-end mb-10">
            <Reveal>
              <div>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-300)] mb-3">
                  Pricing
                </div>
                <h2
                  className="font-display font-semibold tracking-[-0.02em]"
                  style={{ fontSize: "clamp(28px, 4vw, 42px)", lineHeight: 1.12 }}
                >
                  Free where it should be.
                  <br />
                  Priced for your size.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-[17px] leading-relaxed text-white/70 max-w-md">
                We gate seats and compliance surface — never the features your
                business needs to operate.
              </p>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                name: "Free",
                price: "$0",
                cadence: "forever",
                tagline: "For teams under 10",
                highlights: [
                  "Unlimited receipts & invoices",
                  "AI categorization",
                  "Audit-ready export",
                ],
              },
              {
                name: "Team",
                price: "$8",
                cadence: "per user / month, annual",
                tagline: "When you need more structure",
                highlights: [
                  "Everything in Free",
                  "Policies & approvals",
                  "Card-linked reconciliation",
                ],
                featured: true,
              },
              {
                name: "Enterprise",
                price: "Custom",
                cadence: "",
                tagline: "50+ seats · SOC 2 · DPA",
                highlights: ["SSO / SAML + SCIM", "API & webhooks", "Dedicated CSM"],
              },
            ].map((p, idx) => (
              <Reveal key={p.name} delay={idx * 90}>
                <div
                  className={[
                    "rounded-2xl p-6 md:p-7 h-full",
                    p.featured
                      ? "bg-gradient-to-br from-[var(--brand-600)] to-[var(--brand-900)] border border-[var(--brand-500)] shadow-[0_20px_60px_-20px_rgba(46,91,255,0.6)]"
                      : "bg-white/5 border border-white/10",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold uppercase tracking-[0.06em]">{p.name}</div>
                    {p.featured && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-[var(--accent-amber)] text-[var(--ink-950)] px-2 py-0.5 rounded-full">
                        Most popular
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span className="font-display font-semibold text-[40px] leading-none tracking-[-0.03em]">
                      {p.price}
                    </span>
                    {p.cadence && (
                      <span className="text-sm text-white/70">{p.cadence}</span>
                    )}
                  </div>
                  <div className="text-sm text-white/70 mb-5">{p.tagline}</div>
                  <ul className="space-y-2 text-sm">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2">
                        <Check
                          className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent-amber)]"
                          strokeWidth={2.5}
                        />
                        <span className="text-white/90">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3 items-center justify-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-white text-[var(--ink-950)] hover:bg-[var(--ink-100)] font-semibold transition-colors"
            >
              Compare plans <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white font-semibold border border-white/20 transition-colors"
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-24 text-center">
        <Reveal>
          <h2
            className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.02em] max-w-[740px] mx-auto"
            style={{ fontSize: "clamp(32px, 5vw, 56px)", lineHeight: 1.08 }}
          >
            Stop rebuilding your books every April.
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-5 text-[18px] text-[var(--ink-700)] max-w-[560px] mx-auto">
            Start with Proofly free. Keep every dollar explainable — continuously.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={ctaHref}
              className="inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white font-semibold shadow-[0_10px_30px_-10px_rgba(10,15,30,0.4)] transition-all"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:hello@proofly.one"
              className="inline-flex items-center justify-center h-12 px-7 rounded-full border border-[var(--ink-200)] bg-white hover:bg-[var(--ink-50)] text-[var(--ink-900)] font-semibold transition-colors"
            >
              Talk to the team
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function AuditStamp({ score }: { score: number }) {
  const RING_TEXT = "· AUDIT-READY · PROOFLY VERIFIED · ";
  const radius = 84;
  const circumference = 2 * Math.PI * radius;
  return (
    <div
      className="relative"
      style={{ width: 220, height: 220 }}
      role="img"
      aria-label={`Audit-ready score: ${score} out of 100`}
    >
      <svg
        viewBox="0 0 220 220"
        width={220}
        height={220}
        className="absolute inset-0 proofly-spin-slow"
      >
        <defs>
          <path
            id="audit-ring-path"
            d={`M 110 110 m -${radius}, 0 a ${radius},${radius} 0 1,1 ${radius * 2},0 a ${radius},${radius} 0 1,1 -${radius * 2},0`}
          />
        </defs>
        <text
          fill="#0A0F1E"
          style={{
            fontFamily: "Inter Tight, Inter, sans-serif",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          <textPath
            href="#audit-ring-path"
            startOffset="0"
            lengthAdjust="spacingAndGlyphs"
            textLength={circumference}
          >
            {RING_TEXT.repeat(3)}
          </textPath>
        </text>
      </svg>
      <div
        className="absolute inset-[22px] rounded-full flex flex-col items-center justify-center text-center"
        style={{
          background: "radial-gradient(circle at 50% 30%, #EEF2FF 0%, #FFFFFF 70%)",
          border: "1px solid var(--ink-200)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--brand-700)]">
          Audit score
        </div>
        <div className="font-display font-semibold text-[56px] leading-none tracking-[-0.04em] text-[var(--ink-950)] mt-1">
          {score}
        </div>
        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--success)]">
          <Check className="w-3 h-3" strokeWidth={3} />
          Ready
        </div>
      </div>
    </div>
  );
}
